import {
    KeypadContext,
    KeypadContextType,
    ModalClickviewVideo,
    QtcRenderer
} from '@3plearning/question-components-library';
import { useDisclosure } from '@chakra-ui/react';
import {
    css,
    Global
} from '@emotion/react';
import { ErrorBoundary } from '@sentry/react';
import React, {
    useContext,
    useEffect,
    useState
} from 'react';

import { getURLLocale } from '../../../src/i18n/utils/locale-utils';
import WorkedSolution from '../../components/worked-solution/worked-solution';
import { WorkedSolutionCloseButton } from '../../containers/question-container/sub-components/question-activity/question-activity.styled';
import {
    getQueryParam,
    transformSchema
} from '../../helpers/utils';
import { ContentType } from '../../routes/milestone-transition/sub-components/intro-screen/intro-screen.types';
import {
    useNav,
    useQuestionSet
} from './preview.hooks';
import {
    PreviewCtnr,
    QuestionCtnr,
    StageCtnr
} from './preview.styled';
import { QuestionFooter } from './question-footer';
import { QuestionHeader } from './question-header';
import { WorkedSolutionErrorFallback } from './worked-solution-error-fallback';

export const Preview = () => {
    const [isWorkedSolutionActivated, setIsWorkedSolutionActivated] = useState<boolean>(false);
    const questionSetId = getQueryParam('questionSetId') || '';
    const {
        questionSet,
        getQuestionSet,
        questions
    } = useQuestionSet();
    const {
        questionNumber,
        navigate
    } = useNav(questions && questions.length || 0);
    const {
        keypadRef, questionBodyRef
    } = useContext(KeypadContext) as KeypadContextType;
    const { ...chakraModalProps } = useDisclosure();
    const conceptRefreshContent = transformSchema(questionSet?.conceptRefresh);
    const conceptRefreshUrl = conceptRefreshContent && [...conceptRefreshContent[0].content]?.pop()?.text || '';
    const currentQuestion = questions?.[questionNumber];
    const workedSolutionDocs = currentQuestion?.answerContent.workedSolution;

    useEffect(() => {
        getQuestionSet(questionSetId, getURLLocale());
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <Global
                styles={css`
                    body {
                        background-color: transparent !important;
                    }

                    @media (min-width: 950px) {
                        body .chakra-popover__content[data-testid='hint-popover'] {
                            top: 30px;
                            left: -30px;
                        }
                    }
                `}
            />
            <PreviewCtnr>
                <StageCtnr>
                    <ModalClickviewVideo
                        url={conceptRefreshUrl}
                        contentType={ContentType.conceptRefresh}
                        {...chakraModalProps}
                        onClose={chakraModalProps.onClose}
                    />
                    {
                        isWorkedSolutionActivated && workedSolutionDocs && (
                            <ErrorBoundary
                                fallback={
                                    <WorkedSolutionErrorFallback
                                        onToastShown={() => setIsWorkedSolutionActivated(false)}
                                    />
                                }
                            >
                                <WorkedSolutionCloseButton
                                    className={'preview-worked-solution-close-btn'}
                                    colorScheme={'white'}
                                    variant={'outline'}
                                    aria-label={'Worked Solution close button'}
                                    onClick={() => setIsWorkedSolutionActivated(false)}
                                />
                                <WorkedSolution
                                    multiDocs={workedSolutionDocs}
                                />
                            </ErrorBoundary>
                        )
                    }
                    {currentQuestion && (
                        <QuestionHeader
                            navigate={navigate}
                            totalQuestions={questions?.length || 0}
                            questionNumber={questionNumber}
                            hintContent={currentQuestion.questionContent.hint}
                        />
                    )}
                    <QuestionCtnr ref={questionBodyRef}>
                        {keypadRef.current && currentQuestion && <QtcRenderer
                            key={questionNumber}
                            callback={() => null}
                            question={currentQuestion.questionContent.question}
                            questionNumber={questionNumber + 1}
                            keypadContainer={keypadRef.current}
                        />}
                    </QuestionCtnr>
                    <QuestionFooter
                        onConceptRefreshOpen={chakraModalProps.onOpen}
                        isConceptRefreshDisabled={!conceptRefreshUrl}
                        workedSolutionDocs={workedSolutionDocs}
                        setIsWorkedSolutionActivated={setIsWorkedSolutionActivated}
                        isWorkedSolutionActivated={isWorkedSolutionActivated}
                    />
                </StageCtnr>
            </PreviewCtnr>
        </>
    );
};
